import { ApiService } from './api';
import { ApiResponse } from '../types/auth';
import {
  AdminDriversResponse,
  AdminDriverDetailsResponse,
  DriversRequest,
  ChangeDriverApplicationStatusRequest,
  ChangeDriverBalanceRequest,
  DriversMapSearchRequest,
  DriverLocationResponse,
  DriverTransactionsResponse,
  CreateDriverTransactionRequest,
  CreateDriverTransactionResponse
} from '../types/drivers';

export class DriversService {
  private apiService: ApiService;

  constructor() {
    this.apiService = ApiService.getInstance();
  }

  async getDrivers(request: DriversRequest): Promise<ApiResponse<AdminDriversResponse[]>> {
    try {
      const params = new URLSearchParams({
        searchQuery: request.searchQuery,
        page: request.page.toString(),
        pageSize: request.pageSize.toString(),
      });

      if (request.applicationStatus) {
        params.append('applicationStatus', request.applicationStatus);
      }

      if (request.driverState) {
        params.append('driverState', request.driverState);
      }

      const response = await this.apiService.get<AdminDriversResponse[]>(
        `/admin/drivers?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch drivers',
        success: false
      };
    }
  }

  async getDriverDetails(driverId: number): Promise<ApiResponse<AdminDriverDetailsResponse>> {
    try {
      const response = await this.apiService.get<AdminDriverDetailsResponse>(
        `/admin/drivers/${driverId}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch driver details',
        success: false
      };
    }
  }

  async changeApplicationStatus(request: ChangeDriverApplicationStatusRequest): Promise<ApiResponse<void>> {
    try {
      await this.apiService.post<void>(
        '/admin/drivers/change-application-status',
        request
      );

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to change application status',
        success: false
      };
    }
  }

  async changeBalance(request: ChangeDriverBalanceRequest): Promise<ApiResponse<void>> {
    try {
      const formData = new URLSearchParams();
      formData.append('balance', request.balance.toString());

      await this.apiService.post<void>(
        `/admin/drivers/${request.driverId}/change-balance`,
        formData.toString(),
        'application/x-www-form-urlencoded'
      );

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to change balance',
        success: false
      };
    }
  }

  async getDriverTransactions(
    driverId: number,
    page: number = 0,
    pageSize: number = 20
  ): Promise<ApiResponse<DriverTransactionsResponse>> {
    try {
      const params = new URLSearchParams({
        driverId: driverId.toString(),
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.apiService.get<DriverTransactionsResponse>(
        `/admin/driver-transactions?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch driver transactions',
        success: false
      };
    }
  }

  async createDriverTransaction(
    request: CreateDriverTransactionRequest
  ): Promise<ApiResponse<CreateDriverTransactionResponse>> {
    try {
      const response = await this.apiService.post<CreateDriverTransactionResponse>(
        '/admin/driver-transactions',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to create driver transaction',
        success: false
      };
    }
  }

  async deleteDriverTransaction(transactionId: number): Promise<ApiResponse<void>> {
    try {
      await this.apiService.delete<void>(
        `/admin/driver-transactions/${transactionId}`
      );

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to delete driver transaction',
        success: false
      };
    }
  }

  async searchDriversOnMap(request: DriversMapSearchRequest): Promise<ApiResponse<DriverLocationResponse[]>> {
    try {
      const response = await this.apiService.post<DriverLocationResponse[]>(
        '/admin/drivers/map-search',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to search drivers on map',
        success: false
      };
    }
  }
}

export const driversService = new DriversService();
